import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Search,
  Upload,
  X,
  Sparkles,
  Copy,
  RotateCcw,
  Loader2,
  Unplug,
  Receipt,
  PackageX,
  Gavel,
  FileText,
  ClipboardList,
  CalendarClock,
  Route,
  ShieldAlert,
  ScanSearch,
  Building2,
  BadgeCheck,
  Brain,
  CheckCircle2,
  Lightbulb,
  Send,
  Clock3,
  MessageSquareText,
  AlertTriangle,
  Paperclip,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import OfficialChannelsCard from "@/components/OfficialChannelsCard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ISSUE_TYPES,
  getProblemOptions,
  getProblemDisplayLabel,
  buildCancellationAdditionalDescription,
  filterCompatibleCancellationAdditionalKeys,
  getCompatibleCancellationAdditionalDetails,
} from "@/data/problems";
import {
  COMPANY_GROUPS,
  ALL_COMPANIES,
  COMPANY_CATEGORIES,
  filterCompanyGroups,
  getCompanyInstitutionContext,
} from "@/data/companies";
import { SOLUTIONS } from "@/data/solutions";
import {
  ACTIVE_BACKEND_ISSUES,
  FLOW_CONFIG,
  FLOW_VARIANTS,
  INVOICE_CATEGORIES,
  PRODUCT_OUTCOMES,
  PRODUCT_PROBLEMS,
  REFUND_CATEGORIES,
  TELECOM_GATE_OPTIONS,
  buildFlowDescription,
  filterGroupsForFlow,
} from "@/data/v1Flows";
import { getInstitutionDeliveryProfile } from "@/data/institutionChannels";
import {
  getBillingProviderOptions,
  isDigitalInstitutionContext,
} from "@/data/digitalSubscriptions";
import {
  COMMERCE_DEFECTIVE_OUTCOMES,
  COMMERCE_INFO_FIELDS,
  COMMERCE_PROBLEMS,
  COMMERCE_PROBLEM_OPTIONS,
  PURCHASE_ACCOUNT_MODE_OPTIONS,
  buildCommerceDescription,
  getCommerceProblemOption,
  isEcommerceInstitutionContext,
} from "@/data/ecommerce";
import {
  buildApplicationPayload,
  createEmptyApplicationInfo,
  getSubscriptionField,
  isCaseResult,
  mapGenerateValidationErrors,
  setSubscriptionUnknown,
  validateApplicationInfo,
} from "@/lib/applicationInfo";
import {
  copyTextSafely,
  createCancellationEmailBody,
  createEmailSubject,
  createV1ApplicationEmailBody,
  stripDigitalSignature,
  stripDigitalCancellationSignature,
} from "@/lib/applicationDelivery";
import {
  DELIVERY_CHANNEL_KINDS,
  DELIVERY_STATES,
  createConfirmedSubmissionRecord,
} from "@/lib/deliveryState";
import { UPLOAD_ACCEPT, validateSelectedFiles } from "@/lib/fileValidation";
import {
  V1_SCOPE_STATUS,
  V1_SOON_CONTENT,
  filterV1CompanyGroups,
  getV1CategoryStatus,
  hasV1VerifiedChannel,
  isV1BillingProviderSupported,
} from "@/config/v1Scope";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ISSUE_ICON = {
  iptal: Unplug,
  fatura: Receipt,
  iade: PackageX,
  hakem: Gavel,
};

const TOTAL_STEPS = 6;
const AUTO_ADVANCE_STEPS = new Set([1, 2, 3]);
const STEP_LABELS = {
  1: "İşlem Türü",
  2: "Kurum Seçimi",
  3: "Sorun Detayı",
  4: "Başvuru Bilgileri",
  5: "Belgeler",
  6: "Ek Durumlar",
};

const CANCELLATION_CATEGORY_LABELS = {
  internet: "İnternet",
  tv: "TV / Yayın",
  mobile: "Mobil",
  digital: "Dijital",
  gym: "Spor Salonu",
  education: "Eğitim",
  insurance: "Sigorta / BES",
  other: "Diğer",
};

function getStepLabel(step, issue) {
  return STEP_LABELS[step];
}

function hasApplicationData(values) {
  return Object.values(values).some((value) => (
    typeof value === "boolean" ? value : String(value).trim().length > 0
  ));
}

export default function Workflow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const requestedIssue = ACTIVE_BACKEND_ISSUES.includes(params.get("issue")) ? params.get("issue") : null;
  const flow = params.get("flow")
    || (requestedIssue === "iptal" ? FLOW_VARIANTS.CANCELLATION
      : requestedIssue === "fatura" ? FLOW_VARIANTS.INVOICE
        : requestedIssue === "iade" ? FLOW_VARIANTS.REFUND
          : null);
  const entryScreen = params.get("screen") || (
    ["fatura", "iade"].includes(requestedIssue)
    && [FLOW_VARIANTS.INVOICE, FLOW_VARIANTS.REFUND].includes(flow)
    && !params.get("company")
    && !params.get("category")
      ? "category"
      : null
  );

  const [step, setStep] = useState(1);
  const [issue, setIssue] = useState(requestedIssue);
  const [company, setCompany] = useState(() => (
    hasV1VerifiedChannel(params.get("company"), requestedIssue) ? params.get("company") : null
  ));
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyCategory, setCompanyCategory] = useState(params.get("category") || null);
  const [billingProvider, setBillingProvider] = useState(null);
  const [commerceProblem, setCommerceProblem] = useState(null);
  const [purchaseAccountMode, setPurchaseAccountMode] = useState(null);
  const [commerceInfo, setCommerceInfo] = useState({});
  const [problemDetail, setProblemDetail] = useState(null);
  const [problemChoice, setProblemChoice] = useState(null);
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [additionalDetailKeys, setAdditionalDetailKeys] = useState([]);
  const [additionalFreeText, setAdditionalFreeText] = useState("");
  const [desiredOutcome, setDesiredOutcome] = useState(() => {
    const outcome = PRODUCT_OUTCOMES.find((item) => item.id === params.get("outcome"));
    return outcome || null;
  });
  const [applicationInfo, setApplicationInfo] = useState(createEmptyApplicationInfo);
  const [issueInfo, setIssueInfo] = useState({});
  const [applicationErrors, setApplicationErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [soonOption, setSoonOption] = useState(null);
  const applicationFormRef = useRef(null);
  const workflowScrollRef = useRef(null);
  const stepContentRef = useRef(null);
  const submittingRef = useRef(false);
  const transitionTimerRef = useRef(null);
  const submissionSequenceRef = useRef(0);
  const requestControllerRef = useRef(null);
  const pendingApplicationFocusRef = useRef(false);
  const previousStepRef = useRef(step);
  const activeSolution = SOLUTIONS.find((solution) => solution.slug === params.get("solution"));

  const subscriptionField = useMemo(
    () => getSubscriptionField(company, ALL_COMPANIES),
    [company]
  );
  const selectedCompanyContext = useMemo(
    () => getCompanyInstitutionContext(company),
    [company]
  );
  const requiresBillingProvider = isDigitalInstitutionContext(selectedCompanyContext);
  const requiresCommerceProblem = isEcommerceInstitutionContext(selectedCompanyContext);

  // SEO links keep their existing direct jump. Discovery cards briefly show the
  // selected real issue value, then use the established 250 ms advance behavior.
  useEffect(() => {
    if (entryScreen) return undefined;
    if (params.get("source") === "discovery" && params.get("issue")) {
      setStep(1);
      setTransitioning(true);
      transitionTimerRef.current = setTimeout(() => {
        transitionTimerRef.current = null;
        setStep(2);
        setTransitioning(false);
      }, 250);
      return () => clearTimeout(transitionTimerRef.current);
    }
    const routeCompany = params.get("company");
    if (params.get("issue") && hasV1VerifiedChannel(routeCompany, requestedIssue)) {
      const context = getCompanyInstitutionContext(routeCompany);
      setStep(isDigitalInstitutionContext(context) || isEcommerceInstitutionContext(context) ? 2 : 3);
    }
    else if (params.get("issue")) setStep(2);
    return undefined;
  }, [entryScreen, params, requestedIssue]);

  useEffect(() => () => {
    clearTimeout(transitionTimerRef.current);
    requestControllerRef.current?.abort();
  }, []);

  useLayoutEffect(() => {
    if (previousStepRef.current === step || analyzing) return undefined;
    previousStepRef.current = step;
    const frame = requestAnimationFrame(() => {
      const scrollRoot = workflowScrollRef.current;
      if (scrollRoot) {
        const left = scrollRoot.scrollLeft;
        if (typeof scrollRoot.scrollTo === "function") {
          scrollRoot.scrollTo({ top: 0, left, behavior: "auto" });
        } else {
          scrollRoot.scrollTop = 0;
        }
      }
      window.scrollTo({ top: 0, left: window.scrollX, behavior: "auto" });
      stepContentRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [step, analyzing]);

  useEffect(() => {
    const applicationStep = 4;
    if (step !== applicationStep || analyzing || !pendingApplicationFocusRef.current) return undefined;
    pendingApplicationFocusRef.current = false;
    const focusTimer = setTimeout(() => {
      applicationFormRef.current?.querySelector('[aria-invalid="true"]')?.focus();
    }, 50);
    return () => clearTimeout(focusTimer);
  }, [step, analyzing, issue]);

  const filteredGroups = useMemo(() => {
    const solutionGroups = activeSolution?.companyGroups?.length
      ? COMPANY_GROUPS.filter((group) => activeSolution.companyGroups.includes(group.id))
      : COMPANY_GROUPS;
    const applyCategory = (groups) => {
      if ([FLOW_VARIANTS.INVOICE, FLOW_VARIANTS.REFUND].includes(flow)) {
        return filterGroupsForFlow(groups, flow, companyCategory);
      }
      return filterCompanyGroups(groups, companyCategory);
    };
    if (!companyQuery.trim()) return filterV1CompanyGroups(applyCategory(solutionGroups), issue);
    const q = companyQuery.trim().toLocaleLowerCase("tr");
    const queryFiltered = solutionGroups.map((g) => ({
      ...g,
      items: g.items.filter((x) => x.toLocaleLowerCase("tr").includes(q)),
    })).filter((g) => g.items.length > 0);
    return filterV1CompanyGroups(applyCategory(queryFiltered), issue);
  }, [activeSolution, companyCategory, companyQuery, flow, issue]);

  const openSoon = (option) => setSoonOption(option || { label: "Desteklenmeyen seçenek" });

  const navigateWithParams = (updates, { replace = false } = {}) => {
    const next = new URLSearchParams(params);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") next.delete(key);
      else next.set(key, value);
    });
    navigate(`/dosya-baslat?${next.toString()}`, { replace });
  };

  const beginMappedFlow = ({ nextIssue, nextFlow, screen = null, preset = null }) => {
    if (!ACTIVE_BACKEND_ISSUES.includes(nextIssue)) return;
    setIssue(nextIssue);
    setCompany(null);
    setBillingProvider(null);
    setCommerceProblem(null);
    setPurchaseAccountMode(null);
    setCommerceInfo({});
    setCompanyQuery("");
    setCompanyCategory(null);
    setProblemDetail(null);
    setProblemChoice(null);
    setDesiredOutcome(null);
    setStep(nextIssue === "hakem" ? 2 : 2);
    navigateWithParams({ issue: nextIssue, flow: nextFlow, screen, preset, source: "routed" });
  };

  const handleEntryChoice = (choice) => {
    if (entryScreen === "category") {
      setCompanyCategory(choice.id);
      setStep(2);
      navigateWithParams({ screen: null, category: choice.id, source: "routed" });
      return;
    }
    if (entryScreen === "product-problem") {
      setIssue("iade");
      setProblemChoice(choice.id);
      setProblemDetail(choice.value);
      navigateWithParams({ screen: "desired-outcome", productProblem: choice.id, issue: "iade", flow: FLOW_VARIANTS.PRODUCT_SERVICE, source: "routed" });
      return;
    }
    if (entryScreen === "desired-outcome") {
      setDesiredOutcome(choice);
      setStep(2);
      navigateWithParams({ screen: null, outcome: choice.id, source: "routed" });
      return;
    }
    if (entryScreen === "termination-context") {
      if (choice.id === "cancel") beginMappedFlow({ nextIssue: "iptal", nextFlow: FLOW_VARIANTS.CANCELLATION });
      else beginMappedFlow({ nextIssue: "fatura", nextFlow: FLOW_VARIANTS.INVOICE, screen: "category" });
      return;
    }
    if (entryScreen === "primary-intent") {
      if (choice.id === "cancel") beginMappedFlow({ nextIssue: "iptal", nextFlow: FLOW_VARIANTS.CANCELLATION });
      if (choice.id === "invoice") beginMappedFlow({ nextIssue: "fatura", nextFlow: FLOW_VARIANTS.INVOICE, screen: "category" });
      if (choice.id === "refund") beginMappedFlow({ nextIssue: "iade", nextFlow: FLOW_VARIANTS.REFUND, screen: "category" });
      if (choice.id === "service") beginMappedFlow({ nextIssue: "iade", nextFlow: FLOW_VARIANTS.PRODUCT_SERVICE, screen: "product-problem" });
      return;
    }
    if (entryScreen === "telecom-gate") {
      if (choice.id === "cancel") beginMappedFlow({ nextIssue: "iptal", nextFlow: FLOW_VARIANTS.CANCELLATION });
      if (choice.id === "invoice") beginMappedFlow({ nextIssue: "fatura", nextFlow: FLOW_VARIANTS.INVOICE, screen: "category" });
      if (choice.id === "service") beginMappedFlow({ nextIssue: "iade", nextFlow: FLOW_VARIANTS.PRODUCT_SERVICE, screen: "product-problem", preset: "poor-service" });
      if (choice.id === "device") beginMappedFlow({ nextIssue: "iade", nextFlow: FLOW_VARIANTS.PRODUCT_SERVICE, screen: "product-problem", preset: "defective" });
      if (choice.id === "termination") navigateWithParams({ screen: "termination-context" });
      if (["unreachable", "other"].includes(choice.id)) navigateWithParams({ screen: "primary-intent" });
    }
  };

  const handleIssueSelection = (value) => {
    if (value === "fatura") {
      beginMappedFlow({ nextIssue: "fatura", nextFlow: FLOW_VARIANTS.INVOICE, screen: "category" });
      return;
    }
    if (value === "iade") {
      beginMappedFlow({ nextIssue: "iade", nextFlow: FLOW_VARIANTS.REFUND, screen: "category" });
      return;
    }
    selectAndAdvance(() => setIssue(value));
  };

  const canProceed =
    (step === 1 && !!issue) ||
    (step === 2 && !!company
      && (!requiresBillingProvider || !!billingProvider)
      && (!requiresCommerceProblem || !!commerceProblem)
      && (selectedCompanyContext?.companyId !== "pttavm" || !!purchaseAccountMode)) ||
    (step === 3 && !!problemDetail) ||
    step === 4 || step === 5 || step === 6;

  const goNext = () => {
    if (!canProceed) return;
    const applicationStep = 4;
    if (step === applicationStep) {
      const errors = validateApplicationInfo(applicationInfo);
      setApplicationErrors(errors);
      if (Object.keys(errors).length) {
        requestAnimationFrame(() => {
          applicationFormRef.current?.querySelector('[aria-invalid="true"]')?.focus();
        });
        return;
      }
    }
    if (step === 6) {
      setReviewOpen(true);
      return;
    }
    setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };
  const goPrev = () => {
    clearTimeout(transitionTimerRef.current);
    setTransitioning(false);
    setStep((s) => Math.max(1, s - 1));
  };

  const selectAndAdvance = (updateSelection) => {
    if (transitioning || transitionTimerRef.current) return;
    setTransitioning(true);
    updateSelection();
    transitionTimerRef.current = setTimeout(() => {
      transitionTimerRef.current = null;
      setStep((current) => Math.min(TOTAL_STEPS, current + 1));
      setTransitioning(false);
    }, 250);
  };

  const selectProblemAndAdvance = (option) => {
    selectAndAdvance(() => {
      setProblemChoice(option.id);
      setProblemDetail(option.value);
      if (issue !== "iptal") return;
      const compatibleKeys = filterCompatibleCancellationAdditionalKeys(
        option.id,
        additionalDetailKeys
      );
      const compatibleFreeText = compatibleKeys.includes("other") ? additionalFreeText : "";
      setAdditionalDetailKeys(compatibleKeys);
      setAdditionalFreeText(compatibleFreeText);
      setDescription(buildCancellationAdditionalDescription(
        compatibleKeys,
        compatibleFreeText,
        option.id
      ));
    });
  };

  const advanceCommerceSelection = (nextProblem, label, followupResolved = false) => {
    if (transitioning || transitionTimerRef.current) return;
    setTransitioning(true);
    setProblemChoice(nextProblem);
    setProblemDetail(label);
    transitionTimerRef.current = setTimeout(() => {
      transitionTimerRef.current = null;
      setStep(!followupResolved && (nextProblem === COMMERCE_PROBLEMS.DEFECTIVE_OR_WRONG_PRODUCT
        || nextProblem === COMMERCE_PROBLEMS.UNREACHABLE) ? 3 : 4);
      setTransitioning(false);
    }, 250);
  };

  const chooseCommerceProblem = (value) => {
    if (transitioning || transitionTimerRef.current) return;
    const label = getCommerceProblemOption(value)?.label || value;
    setCommerceProblem(value);
    setDesiredOutcome(null);
    if (selectedCompanyContext?.companyId === "pttavm" && !purchaseAccountMode) return;
    advanceCommerceSelection(value, label);
  };

  const choosePurchaseAccountMode = (value) => {
    setPurchaseAccountMode(value);
    const label = getCommerceProblemOption(commerceProblem)?.label || commerceProblem;
    advanceCommerceSelection(commerceProblem, label);
  };

  const submit = async () => {
    if (submittingRef.current) return;
    const context = getCompanyInstitutionContext(company);
    const deliveryProfile = getInstitutionDeliveryProfile(issue, context?.companyId || company, {
      serviceScope: context?.serviceScope,
      billingProvider,
      commerceProblem,
      purchaseAccountMode,
    });
    if (!context?.companyId || !deliveryProfile?.channelType) {
      openSoon({ label: company || "Kurum kanalı" });
      return;
    }
    submittingRef.current = true;
    const submissionSequence = ++submissionSequenceRef.current;
    const controller = new AbortController();
    requestControllerRef.current = controller;
    setLoading(true);
    setAnalyzing(true);
    const started = Date.now();
    try {
      const flowConfig = FLOW_CONFIG[flow];
      const selectedAdditionalLabels = (flowConfig?.problemOptions || [])
        .filter((option) => additionalDetailKeys.includes(option.id))
        .map((option) => option.label);
      const flowDescription = flowConfig
        ? buildFlowDescription({
          flow,
          problemLabel: flowConfig.problemOptions.find((option) => option.id === problemChoice)?.label || problemDetail,
          desiredOutcome: desiredOutcome?.sentence,
          info: issueInfo,
          selectedLabels: selectedAdditionalLabels,
          freeText: additionalFreeText,
        })
        : description;
      const commerceDescription = requiresCommerceProblem
        ? buildCommerceDescription({ commerceProblem, desiredOutcome, info: commerceInfo })
        : "";
      const safeDescription = [flowDescription, commerceDescription].filter(Boolean).join(" ");
      const payload = {
        issue_type: issue,
        company,
        problem_detail: problemDetail,
        user_description: safeDescription,
        ...buildApplicationPayload(applicationInfo, subscriptionField),
        uploaded_files: files.map((f) => ({
          name: f.name,
          size: f.size,
          type: f.type || null,
        })),
      };
      const { data } = await axios.post(`${API}/case/generate`, payload, {
        timeout: 15000,
        signal: controller.signal,
      });
      // Ensure a minimum agent-analysis feel (~2.4s) even on fast responses.
      const elapsed = Date.now() - started;
      const MIN_ANALYZE_MS = 2400;
      if (elapsed < MIN_ANALYZE_MS) {
        await new Promise((r) => setTimeout(r, MIN_ANALYZE_MS - elapsed));
      }
      if (submissionSequence !== submissionSequenceRef.current) return;
      if (!isCaseResult(data)) throw new Error("invalid_generate_response");
      setResult(data);
      setStep(7);
    } catch (e) {
      if (submissionSequence !== submissionSequenceRef.current || axios.isCancel(e)) return;
      const fieldErrors = mapGenerateValidationErrors(e);
      if (Object.keys(fieldErrors).length) {
        setApplicationErrors((current) => ({ ...current, ...fieldErrors }));
        pendingApplicationFocusRef.current = true;
        setStep(4);
        toast.error("Bazı başvuru bilgilerini kontrol etmeniz gerekiyor.");
      } else {
        toast.error("Başvurunuz hazırlanamadı. Bilgileriniz korundu; lütfen tekrar deneyin.");
      }
    } finally {
      if (submissionSequence === submissionSequenceRef.current) {
        submittingRef.current = false;
        requestControllerRef.current = null;
        setLoading(false);
        setAnalyzing(false);
      }
    }
  };

  const reset = () => {
    clearTimeout(transitionTimerRef.current);
    transitionTimerRef.current = null;
    submissionSequenceRef.current += 1;
    requestControllerRef.current?.abort();
    requestControllerRef.current = null;
    submittingRef.current = false;
    pendingApplicationFocusRef.current = false;
    setStep(1);
    setIssue(null);
    setCompany(null);
    setBillingProvider(null);
    setCommerceProblem(null);
    setPurchaseAccountMode(null);
    setCommerceInfo({});
    setCompanyQuery("");
    setCompanyCategory(null);
    setProblemDetail(null);
    setProblemChoice(null);
    setFiles([]);
    setDescription("");
    setAdditionalDetailKeys([]);
    setAdditionalFreeText("");
    setApplicationInfo(createEmptyApplicationInfo());
    setIssueInfo({});
    setDesiredOutcome(null);
    setApplicationErrors({});
    setResult(null);
    setLoading(false);
    setAnalyzing(false);
    setTransitioning(false);
    setStartConfirmOpen(false);
    setReviewOpen(false);
    setSoonOption(null);
  };

  const startNewFile = () => {
    reset();
    navigate("/dosya-baslat", { replace: true });
  };

  const handleHeaderStart = () => {
    const hasMeaningfulData = !!(
      issue
      || company
      || billingProvider
      || commerceProblem
      || purchaseAccountMode
      || problemDetail
      || files.length
      || description.trim()
      || hasApplicationData(applicationInfo)
      || result
    );
    if (hasMeaningfulData) setStartConfirmOpen(true);
    else startNewFile();
  };

  return (
    <div ref={workflowScrollRef} data-testid="workflow-scroll-root" className="fs-focus-shell">
      <SEO
        title="Başvuru Hazırla | Abonelik İptal"
        description="Abonelik iptali, fatura itirazı, iade veya Tüketici Hakem Heyeti başvurunuzu Abonelik İptal ile hazırlayın."
        path="/dosya-baslat"
      />
      <Header onStart={handleHeaderStart} />
      <div className={`${step === 7 ? "max-w-5xl" : "max-w-4xl"} mx-auto md:px-8 md:pt-10 md:pb-20`}>
        <div className="fs-focus-panel px-5 py-7 md:px-9 md:py-9 min-h-[calc(100vh_-_4rem)] md:min-h-0">
        {step <= TOTAL_STEPS && !analyzing && (
          <div className="mb-6 flex items-center gap-3 border-b border-slate-100 pb-5">
            <button
              type="button"
              onClick={() => (entryScreen ? navigate(-1) : step > 1 ? goPrev() : navigate(-1))}
              aria-label="Önceki ekrana dön"
              className="w-10 h-10 shrink-0 rounded-full border border-slate-200 bg-white flex items-center justify-center text-[var(--feshet-petrol)] hover:border-[var(--feshet-teal)]"
            >
              <ArrowLeft size={18} />
            </button>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--feshet-teal)]">Başvuru hazırlığı</div>
              <div className="truncate font-bold text-[var(--feshet-text)]">{activeSolution?.title || "Yeni başvuru"}</div>
            </div>
            <button
              type="button"
              onClick={() => { reset(); navigate("/"); }}
              aria-label="Çözüm penceresini kapat"
              data-testid="workflow-close-btn"
              className="ml-auto w-10 h-10 shrink-0 rounded-full text-slate-500 hover:bg-slate-100 flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {step <= TOTAL_STEPS && !analyzing && !entryScreen && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div className="min-w-0 pr-3 text-xs font-bold uppercase tracking-[0.16em] sm:tracking-[0.2em] text-[#14A89F]">
                <span>Adım {step} / {TOTAL_STEPS}</span>
                <span className="block sm:inline">
                  <span className="hidden sm:inline"> · </span>{getStepLabel(step, issue)}
                </span>
              </div>
              <span className="text-xs font-semibold text-slate-500">{getStepLabel(step, issue)}</span>
            </div>
            <Progress
              data-testid="workflow-progress"
              value={(step / TOTAL_STEPS) * 100}
              className="h-1.5 bg-slate-200 [&>div]:bg-[#14A89F]"
            />
          </>
        )}

        <AnimatePresence mode="wait">
          {analyzing ? (
            <motion.div
              key="analyzing"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.25 }}
              className="mt-8"
            >
              <AgentAnalyzing />
            </motion.div>
          ) : (
            <motion.div
              key={entryScreen || step}
              ref={stepContentRef}
              tabIndex={-1}
              role="region"
              aria-label={step <= TOTAL_STEPS ? `${getStepLabel(step, issue)} adımı` : "Başvuru sonucu"}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.25 }}
              className="mt-8 outline-none scroll-mt-28"
            >
              {entryScreen && (
                <EntryDecision
                  screen={entryScreen}
                  flow={flow}
                  preset={params.get("preset")}
                  onChoice={handleEntryChoice}
                  onSoon={openSoon}
                />
              )}
              {!entryScreen && step === 1 && (
                <Step1
                  issue={issue}
                  setIssue={handleIssueSelection}
                  disabled={transitioning}
                />
              )}
            {!entryScreen && step === 2 && (
              <Step2
                company={company}
                setCompany={(nextCompany) => {
                  if (!hasV1VerifiedChannel(nextCompany, issue)) {
                    openSoon({ label: nextCompany });
                    return;
                  }
                  const updateCompany = () => {
                  const nextField = getSubscriptionField(nextCompany, ALL_COMPANIES);
                  if (applicationInfo.subscriptionNumber && nextField.label !== subscriptionField.label) {
                    toast.info(`Numara alanı “${nextField.label}” olarak değişti. Girdiğiniz değeri kontrol edin.`);
                  }
                  setCompany(nextCompany);
                  setBillingProvider(null);
                  setCommerceProblem(null);
                  setPurchaseAccountMode(null);
                  setCommerceInfo({});
                  };
                  const nextContext = getCompanyInstitutionContext(nextCompany);
                  if (isDigitalInstitutionContext(nextContext) || isEcommerceInstitutionContext(nextContext)) {
                    if (transitioning || transitionTimerRef.current) return;
                    updateCompany();
                    return;
                  }
                  if (flow === FLOW_VARIANTS.PRODUCT_SERVICE && problemDetail) {
                    if (transitioning || transitionTimerRef.current) return;
                    setTransitioning(true);
                    updateCompany();
                    transitionTimerRef.current = setTimeout(() => {
                      transitionTimerRef.current = null;
                      setStep(4);
                      setTransitioning(false);
                    }, 250);
                  } else selectAndAdvance(updateCompany);
                }}
                disabled={transitioning}
                companyQuery={companyQuery}
                setCompanyQuery={setCompanyQuery}
                filteredGroups={filteredGroups}
                companyCategory={companyCategory}
                setCompanyCategory={(value) => {
                  setCompanyCategory(value);
                  setCompany(null);
                  setBillingProvider(null);
                  setCommerceProblem(null);
                  setPurchaseAccountMode(null);
                  setCommerceInfo({});
                  setCompanyQuery("");
                }}
                billingProvider={billingProvider}
                setBillingProvider={(value) => selectAndAdvance(() => setBillingProvider(value))}
                onSoon={openSoon}
                commerceProblem={commerceProblem}
                setCommerceProblem={chooseCommerceProblem}
                clearCommerceProblem={() => {
                  setCommerceProblem(null);
                  setPurchaseAccountMode(null);
                }}
                purchaseAccountMode={purchaseAccountMode}
                setPurchaseAccountMode={choosePurchaseAccountMode}
                clearCompany={() => {
                  setCompany(null);
                  setBillingProvider(null);
                  setCommerceProblem(null);
                  setPurchaseAccountMode(null);
                  setCommerceInfo({});
                }}
                issue={issue}
                flow={flow}
              />
            )}
            {!entryScreen && step === 3 && (
              requiresCommerceProblem ? (
                <CommerceFollowupDecision
                  commerceProblem={commerceProblem}
                  desiredOutcome={desiredOutcome}
                  disabled={transitioning}
                  onOutcome={(option) => {
                    setDesiredOutcome(option);
                    advanceCommerceSelection(commerceProblem, getCommerceProblemOption(commerceProblem)?.label || commerceProblem, true);
                  }}
                  onIntent={(value) => {
                    setCommerceProblem(value);
                    setDesiredOutcome(null);
                    advanceCommerceSelection(value, getCommerceProblemOption(value)?.label || value);
                  }}
                />
              ) : <Step3
                issue={issue}
                flow={flow}
                problemDetail={problemDetail}
                problemChoice={problemChoice}
                setProblem={selectProblemAndAdvance}
                disabled={transitioning}
              />
            )}
            {!entryScreen && step === 4 && (
              <div>
                <Step5
                  ref={applicationFormRef}
                  values={applicationInfo}
                  setValues={setApplicationInfo}
                  errors={applicationErrors}
                  setErrors={setApplicationErrors}
                  subscriptionField={subscriptionField}
                />
                {FLOW_CONFIG[flow] && (
                  <FlowInformationFields
                    flow={flow}
                    values={issueInfo}
                    setValues={setIssueInfo}
                  />
                )}
                {requiresCommerceProblem && (
                  <CommerceInformationFields values={commerceInfo} setValues={setCommerceInfo} />
                )}
              </div>
            )}
            {!entryScreen && step === 5 && (
              <Step4 files={files} setFiles={setFiles} flow={flow} />
            )}
            {!entryScreen && step === 6 && (
              <Step6
                issue={issue}
                flow={flow}
                problemChoice={problemChoice}
                issueInfo={issueInfo}
                desiredOutcome={desiredOutcome}
                description={description}
                setDescription={setDescription}
                selectedKeys={additionalDetailKeys}
                setSelectedKeys={setAdditionalDetailKeys}
                freeText={additionalFreeText}
                setFreeText={setAdditionalFreeText}
              />
            )}
            {!entryScreen && step === 7 && result && (
              <ResultPage
                result={result}
                onReset={reset}
                context={{
                  issue,
                  flow,
                  company,
                  billingProvider,
                  commerceProblem,
                  purchaseAccountMode,
                  commerceInfo,
                  problemDetail,
                  problemLabel: (FLOW_CONFIG[flow]?.problemOptions || getProblemOptions(issue)).find((option) => option.id === problemChoice)?.label
                    || problemDetail,
                  description,
                  issueInfo,
                  desiredOutcome,
                  additionalDetailKeys,
                  additionalFreeText,
                  hasNationalId: false,
                  applicationInfo,
                  subscriptionField,
                  files,
                }}
              />
            )}
            </motion.div>
          )}
        </AnimatePresence>

        {step <= TOTAL_STEPS && !analyzing && !entryScreen && (
          <div className="mt-10 flex items-center justify-between">
            <button
              onClick={goPrev}
              disabled={step === 1 || transitioning}
              data-testid="workflow-prev-btn"
              className="fs-btn-outline inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ArrowLeft size={16} /> Geri
            </button>
            {!AUTO_ADVANCE_STEPS.has(step) && (
              <button
                onClick={goNext}
                disabled={!canProceed || loading}
                data-testid="workflow-next-btn"
                className="fs-btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Hazırlanıyor
                  </>
                ) : step === 6 ? (
                  <>
                    Başvurumu hazırla <Sparkles size={18} />
                  </>
                ) : (
                  <>
                    Devam <ArrowRight size={18} />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
      </div>
      <ReviewDialog
        open={reviewOpen}
        onOpenChange={setReviewOpen}
        onConfirm={() => {
          setReviewOpen(false);
          submit();
        }}
        issue={issue}
        company={company}
        problemDetail={problemDetail}
        files={files}
        applicationInfo={applicationInfo}
        subscriptionField={subscriptionField}
        loading={loading}
      />
      <AlertDialog open={startConfirmOpen} onOpenChange={setStartConfirmOpen}>
        <AlertDialogContent className="w-[calc(100%_-_2rem)] rounded-3xl border-slate-200 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0F3D4A]">Yeni başvuru başlat</AlertDialogTitle>
            <AlertDialogDescription className="text-[#0F3D4A]/70 leading-relaxed">
              Mevcut başvuruyu bırakıp yeni bir başvuru başlatmak istediğinize emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="continue-current-file-btn" className="rounded-full">
              Başvuruya devam et
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-new-file-btn"
              onClick={startNewFile}
              className="rounded-full bg-[#0F3D4A] hover:bg-[#0F3D4A]/90"
            >
              Yeni başvuru başlat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={!!soonOption} onOpenChange={(open) => { if (!open) setSoonOption(null); }}>
        <AlertDialogContent data-testid="v1-soon-dialog" className="w-[calc(100%_-_2rem)] rounded-3xl border-slate-200 bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>{V1_SOON_CONTENT.title}</AlertDialogTitle>
            <AlertDialogDescription>{V1_SOON_CONTENT.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="v1-soon-back-btn" onClick={() => setSoonOption(null)} className="rounded-full bg-[#0F3D4A] hover:bg-[#0F3D4A]/90">
              Geri dön
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Footer />
    </div>
  );
}

/* ---------- Steps ---------- */

function EntryDecision({ screen, flow, preset, onChoice, onSoon }) {
  let title = "Ne konuda yardım istiyorsunuz?";
  let description = "Size uygun ilk kurum başvurusunu hazırlamak için bir seçim yapın.";
  let options = TELECOM_GATE_OPTIONS;
  if (screen === "category" && flow === FLOW_VARIANTS.INVOICE) {
    title = "Hangi faturayla ilgili sorun yaşıyorsunuz?";
    options = INVOICE_CATEGORIES;
  } else if (screen === "category" && flow === FLOW_VARIANTS.REFUND) {
    title = "Ne için para iadesi istiyorsunuz?";
    options = REFUND_CATEGORIES;
  } else if (screen === "product-problem") {
    title = "Sorun hangi konuyla ilgili?";
    options = PRODUCT_PROBLEMS;
  } else if (screen === "desired-outcome") {
    title = "Nasıl bir çözüm istiyorsunuz?";
    options = PRODUCT_OUTCOMES;
    description = "Seçiminiz başvuru metnine eklenir; işlem türünü değiştirmez.";
  } else if (screen === "termination-context") {
    title = "Cayma bedeli hangi işlemle ilgili?";
    options = [
      { id: "cancel", label: "Aboneliği iptal ederken çıkarıldı" },
      { id: "invoice", label: "Mevcut faturama yansıtıldı" },
    ];
  } else if (screen === "primary-intent") {
    title = "Asıl talebiniz nedir?";
    options = [
      { id: "cancel", label: "Aboneliğimi iptal etmek" },
      { id: "invoice", label: "Faturaya itiraz etmek" },
      { id: "refund", label: "Para iadesi istemek" },
      { id: "service", label: "Ürün veya hizmet sorununu çözmek" },
    ];
  }
  return (
    <div data-testid={`entry-${screen}`}>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">{title}</h1>
      <p className="mt-2 text-[#0F3D4A]/70">{description}</p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const status = screen === "category"
            ? getV1CategoryStatus(flow, option.id)
            : V1_SCOPE_STATUS.ACTIVE;
          return (
          <button
            key={option.id}
            type="button"
            data-testid={`entry-choice-${option.id}`}
            aria-pressed={preset === option.id}
            onClick={() => (status === V1_SCOPE_STATUS.ACTIVE ? onChoice(option) : onSoon(option))}
            className={`fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A] ${preset === option.id ? "fs-card-active" : ""}`}
          >
            <span>{option.label}</span>
            {status === V1_SCOPE_STATUS.SOON && <span className="mt-1 block text-xs font-bold text-[#E76A5A]">Yakında</span>}
          </button>
          );
        })}
      </div>
    </div>
  );
}

function Step1({ issue, setIssue, disabled }) {
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
        Neyi çözmek istiyorsunuz?
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70">
        Size en uygun yolu belirlemek için başlayalım.
      </p>
      <div className="mt-8 grid md:grid-cols-2 gap-4">
        {ISSUE_TYPES.filter((t) => t.id !== "hakem").map((t) => {
          const Icon = ISSUE_ICON[t.id];
          const active = issue === t.id;
          return (
            <button
              key={t.id}
              data-testid={`issue-${t.id}`}
              onClick={() => setIssue(t.id)}
              disabled={disabled}
              aria-pressed={active}
              className={`fs-card p-6 text-left ${active ? "fs-card-active" : ""}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                <Icon size={22} className="text-[#0F3D4A]" strokeWidth={2.25} />
              </div>
              <div className="mt-5 text-lg font-semibold text-[#0F3D4A]">
                {t.title}
              </div>
              <div className="mt-1.5 text-sm text-[#0F3D4A]/70 leading-relaxed">
                {t.subtitle}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BillingProviderDecision({ company, companyId, onSelect, onBack, onSoon, disabled }) {
  const options = getBillingProviderOptions(companyId);
  return (
    <div data-testid="billing-provider-decision">
      <button type="button" onClick={onBack} data-testid="billing-provider-back" className="text-sm font-semibold text-[#0F766E] underline underline-offset-4">
        Platformu değiştir
      </button>
      <h1 className="mt-4 text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
        Üyeliğin ücretini nereden ödüyorsunuz?
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70">
        {company} için doğru resmî iptal veya iade kanalını belirlemek için faturalandırma sağlayıcısını seçin.
      </p>
      {companyId === "max" && (
        <p data-testid="max-migration-note" className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
          BluTV abonelikleri Max hesap ve faturalandırma sistemine taşındı.
        </p>
      )}
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {options.map((option) => {
          const supported = isV1BillingProviderSupported(option.value);
          return (
          <button
            key={option.id}
            type="button"
            data-testid={`billing-provider-${option.testId || option.value}`}
            onClick={() => (supported ? onSelect(option.value) : onSoon(option))}
            disabled={disabled}
            className="fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A]"
          >
            <span>{option.label}</span>
            {!supported && <span className="mt-1 block text-xs font-bold text-[#E76A5A]">Yakında</span>}
          </button>
          );
        })}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-slate-500">
        Bu seçim backend işlem türünü değiştirmez ve Abonelik İptal hiçbir platformun şifresini istemez.
      </p>
    </div>
  );
}

function CommerceProblemDecision({ company, onSelect, onBack, disabled }) {
  return (
    <div data-testid="commerce-problem-decision">
      <button type="button" onClick={onBack} data-testid="commerce-problem-back" className="text-sm font-semibold text-[#0F766E] underline underline-offset-4">
        Platformu değiştir
      </button>
      <h1 className="mt-4 text-3xl font-bold tracking-tight text-[#0F3D4A] md:text-4xl">
        Siparişinizle ilgili ne yapmak istiyorsunuz?
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70">{company} için doğru resmî sipariş veya iade kanalını belirleyelim.</p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {COMMERCE_PROBLEM_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            data-testid={`commerce-problem-${option.value}`}
            onClick={() => onSelect(option.value)}
            disabled={disabled}
            className="fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A]"
          >
            {option.label}
          </button>
        ))}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-slate-500">Bu seçim backend işlem türünü değiştirmez. Abonelik İptal platform şifrenizi istemez ve işlemi sizin adınıza göndermez.</p>
    </div>
  );
}

function PurchaseAccountModeDecision({ onSelect, onBack, disabled }) {
  return (
    <div data-testid="purchase-account-mode-decision">
      <button type="button" onClick={onBack} className="text-sm font-semibold text-[#0F766E] underline underline-offset-4">Sorunu değiştir</button>
      <h1 className="mt-4 text-3xl font-bold tracking-tight text-[#0F3D4A] md:text-4xl">Bu siparişi hesabınızla giriş yaparak mı verdiniz?</h1>
      <div className="mt-8 grid gap-3">
        {PURCHASE_ACCOUNT_MODE_OPTIONS.map((option) => (
          <button key={option.value} type="button" data-testid={`purchase-account-mode-${option.value}`} onClick={() => onSelect(option.value)} disabled={disabled} className="fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A]">
            {option.label}
          </button>
        ))}
      </div>
      <p className="mt-5 text-xs leading-relaxed text-slate-500">Misafir sipariş destek e-postası yalnız “üye olmadan” seçildiğinde kullanılabilir.</p>
    </div>
  );
}

function CommerceFollowupDecision({ commerceProblem, onOutcome, onIntent, disabled }) {
  if (commerceProblem === COMMERCE_PROBLEMS.DEFECTIVE_OR_WRONG_PRODUCT) {
    return (
      <div data-testid="commerce-defective-outcome-decision">
        <h1 className="text-3xl font-bold tracking-tight text-[#0F3D4A] md:text-4xl">Nasıl bir çözüm istiyorsunuz?</h1>
        <p className="mt-2 text-[#0F3D4A]/70">Para iadesi yalnız siz seçerseniz talebe eklenir.</p>
        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          {COMMERCE_DEFECTIVE_OUTCOMES.map((option) => (
            <button key={option.id} type="button" data-testid={`commerce-outcome-${option.id}`} onClick={() => onOutcome(option)} disabled={disabled} className="fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A]">
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  const options = COMMERCE_PROBLEM_OPTIONS.filter((option) => ![
    COMMERCE_PROBLEMS.UNREACHABLE,
  ].includes(option.value));
  return (
    <div data-testid="commerce-unreachable-intent-decision">
      <h1 className="text-3xl font-bold tracking-tight text-[#0F3D4A] md:text-4xl">Asıl talebiniz nedir?</h1>
      <p className="mt-2 text-[#0F3D4A]/70">Ulaşamama sorunundan önce yapmak istediğiniz işlemi seçin.</p>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {options.map((option) => (
          <button key={option.value} type="button" data-testid={`commerce-intent-${option.value}`} onClick={() => onIntent(option.value)} disabled={disabled} className="fs-card min-h-16 px-5 py-5 text-left font-semibold text-[#0F3D4A]">
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Step2({
  company,
  setCompany,
  companyQuery,
  setCompanyQuery,
  filteredGroups,
  companyCategory,
  setCompanyCategory,
  issue,
  flow,
  disabled,
  billingProvider,
  setBillingProvider,
  onSoon,
  commerceProblem,
  setCommerceProblem,
  clearCommerceProblem,
  purchaseAccountMode,
  setPurchaseAccountMode,
  clearCompany,
}) {
  const institutionContext = getCompanyInstitutionContext(company);
  if (company && isDigitalInstitutionContext(institutionContext) && !billingProvider) {
    return (
      <BillingProviderDecision
        company={company}
        companyId={institutionContext.companyId}
        onSelect={setBillingProvider}
        onBack={clearCompany}
        onSoon={onSoon}
        disabled={disabled}
      />
    );
  }
  if (company && isEcommerceInstitutionContext(institutionContext) && !commerceProblem) {
    return <CommerceProblemDecision company={company} onSelect={setCommerceProblem} onBack={clearCompany} disabled={disabled} />;
  }
  if (institutionContext?.companyId === "pttavm" && commerceProblem && !purchaseAccountMode) {
    return (
      <PurchaseAccountModeDecision
        onSelect={setPurchaseAccountMode}
        onBack={clearCommerceProblem}
        disabled={disabled}
      />
    );
  }

  const isCancellationCategoryChoice = issue === "iptal"
    && !companyCategory
    && !companyQuery.trim()
    && !company;

  if (isCancellationCategoryChoice) {
    return (
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
          Hangi hizmeti iptal etmek istiyorsunuz?
        </h1>
        <p className="mt-2 text-[#0F3D4A]/70">
          Önce hizmet türünü seçin; size yalnızca ilgili kurumları gösterelim.
        </p>
        <div className="mt-8 grid sm:grid-cols-2 gap-3" data-testid="service-type-options">
          {COMPANY_CATEGORIES.map((category) => {
            const status = getV1CategoryStatus(FLOW_VARIANTS.CANCELLATION, category.id);
            return (
            <button
              key={category.id}
              type="button"
              data-testid={`company-category-${category.id}`}
              onClick={() => (status === V1_SCOPE_STATUS.ACTIVE ? setCompanyCategory(category.id) : onSoon(category))}
              className="fs-card p-5 text-left text-[#0F3D4A]"
            >
              <span className="font-semibold">{CANCELLATION_CATEGORY_LABELS[category.id] || category.label}</span>
              <span className="mt-1 block text-sm text-[#0F3D4A]/60">{status === V1_SCOPE_STATUS.ACTIVE ? "İlgili kurumları göster" : "Yakında"}</span>
            </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
        {issue === "iptal" ? "Hangi aboneliği iptal etmek istiyorsunuz?" : "Hangi kurum veya şirketle ilgili?"}
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70">
        Kurumu kategorilerden seçin veya adıyla arayın.
      </p>
      {companyCategory && (
        <button
          type="button"
          onClick={() => {
            if ([FLOW_VARIANTS.INVOICE, FLOW_VARIANTS.REFUND].includes(flow)) window.history.back();
            else setCompanyCategory(null);
          }}
          data-testid="change-service-type-btn"
          className="mt-4 text-sm font-semibold text-[#0F766E] underline underline-offset-4"
        >
          {flow === FLOW_VARIANTS.INVOICE ? "Fatura türünü değiştir" : flow === FLOW_VARIANTS.REFUND ? "İade konusunu değiştir" : "Hizmet türünü değiştir"}
        </button>
      )}
      {!([FLOW_VARIANTS.INVOICE, FLOW_VARIANTS.REFUND].includes(flow)) && <div className="mt-5 -mx-5 px-5 md:mx-0 md:px-0 overflow-x-auto fs-hide-scrollbar" aria-label="Kurum kategorileri">
        <div className="flex w-max gap-2 pb-1">
          {COMPANY_CATEGORIES.map((category) => {
            const status = getV1CategoryStatus(FLOW_VARIANTS.CANCELLATION, category.id);
            return (
            <button
              key={category.id}
              type="button"
              data-testid={`company-category-${category.id}`}
              aria-pressed={companyCategory === category.id}
              onClick={() => (status === V1_SCOPE_STATUS.ACTIVE
                ? setCompanyCategory(companyCategory === category.id ? null : category.id)
                : onSoon(category))}
              className={`fs-filter-pill ${companyCategory === category.id ? "fs-filter-pill-active" : ""}`}
            >
              {category.label}{status === V1_SCOPE_STATUS.SOON ? " · Yakında" : ""}
            </button>
            );
          })}
        </div>
      </div>}
      <div className="mt-6 relative">
        <Search
          size={18}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0F3D4A]/50"
        />
        <input
          id="company-search-input"
          value={companyQuery}
          onChange={(e) => setCompanyQuery(e.target.value)}
          placeholder="Kurumunuzu arayın"
          data-testid="company-search-input"
          className="w-full rounded-full bg-white border border-slate-200 pl-11 pr-5 py-3.5 text-[#0F3D4A] placeholder:text-[#0F3D4A]/40 focus:outline-none focus:border-[#4DA6A1]"
        />
      </div>

      <div className="mt-8 space-y-8">
        {filteredGroups.length === 0 ? (
          <div data-testid="company-empty-state" className="fs-card p-6 text-[#0F3D4A]/70">
            <h2 className="font-semibold text-[#0F3D4A]">V1 kapsamında doğrulanmış kurum bulunamadı.</h2>
            <p className="mt-1 text-sm">Yazımı kontrol edin veya desteklenen kurum listesine geri dönün.</p>
            <button
              onClick={() => onSoon({ label: companyQuery || "Başka kurum" })}
              disabled={disabled}
              data-testid="pick-other-btn"
              className="mt-3 font-semibold text-[#E76A5A] underline underline-offset-4"
            >
              Bu kurum için kapsam bilgisini görüntüle
            </button>
          </div>
        ) : (
          filteredGroups.map((g) => (
            <div key={g.id}>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#4DA6A1] mb-3">
                {g.label}
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {g.items.map((name) => {
                  const active = company === name;
                  return (
                    <button
                      key={name}
                      data-testid={`company-${name}`}
                      onClick={() => setCompany(name)}
                      disabled={disabled}
                      aria-pressed={active}
                      className={`fs-card px-4 py-4 text-sm font-semibold text-[#0F3D4A] text-left ${
                        active ? "fs-card-active" : ""
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className="w-9 h-9 rounded-xl bg-white/70 border border-white flex items-center justify-center text-xs font-extrabold text-[var(--feshet-teal)] shrink-0">
                          {name === "Diğer" ? <Building2 size={16} /> : name.charAt(0)}
                        </span>
                        <span>{name}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Step3({ issue, flow, problemDetail, problemChoice, setProblem, disabled }) {
  const config = FLOW_CONFIG[flow];
  const options = config?.problemOptions || getProblemOptions(issue);
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
        {issue === "iptal" ? "İptal sürecinde ne yaşıyorsunuz?" : config?.problemTitle || "Ne sorun yaşıyorsunuz?"}
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70">
        Size özel adımları hazırlamak için sorununuza en yakın olanı seçin.
      </p>
      <div className="mt-8 grid md:grid-cols-2 gap-3">
        {options.map((option) => {
          const active = problemChoice === option.id
            || (issue !== "iptal" && problemDetail === option.value);
          return (
            <button
              key={option.id}
              type="button"
              data-testid={`problem-${option.id}`}
              onClick={() => setProblem(option)}
              onKeyDown={(event) => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                setProblem(option);
              }}
              disabled={disabled}
              aria-pressed={active}
              className={`fs-card px-5 py-5 text-left text-[#0F3D4A] ${
                active ? "fs-card-active" : ""
              }`}
            >
              <span className="font-semibold">{option.label}</span>
            </button>
          );
        })}
      </div>
      {problemChoice && (
        <div data-testid="cancellation-agent-note" className="mt-6 rounded-3xl bg-[#0F172A] p-5 text-white">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#22D3CE]">
            <Brain size={15} /> Abonelik İptal Asistanı
          </div>
          <p className="mt-2 text-sm leading-relaxed text-white/85">
            {config?.agentNote || "Seçiminize göre önce kuruma yazılı iptal talebi göndereceğiz. Yanıtı ve işlem tarihini saklayarak sonraki adımı gerektiğinde birlikte belirleyebilirsiniz."}
          </p>
        </div>
      )}
    </div>
  );
}

function Step4({ files, setFiles, flow }) {
  const [fileError, setFileError] = useState("");
  const documentTypes = FLOW_CONFIG[flow]?.documents
    || ["Fatura", "Sözleşme", "Ekran görüntüsü", "Önceki başvuru veya cevap", "Başka belge"];
  const onFiles = (list) => {
    const { acceptedFiles, error } = validateSelectedFiles(list);
    setFileError(error);
    setFiles((prev) => [...prev, ...acceptedFiles].slice(0, 6));
  };
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">
        Elinizde belge var mı?
      </h1>
      <p className="mt-2 text-[#0F3D4A]/70 leading-relaxed">
        Fatura, sözleşme, ekran görüntüsü veya önceki başvurularınızı ekleyebilirsiniz. Bu adım zorunlu değildir.
      </p>
      <div className="mt-5 flex flex-wrap gap-2" aria-label="Eklenebilecek belge türleri">
        {documentTypes.map((type) => (
          <span key={type} className="rounded-full bg-[var(--feshet-ice)]/60 px-3 py-1.5 text-xs font-semibold text-[var(--feshet-petrol)]">{type}</span>
        ))}
      </div>

      <label
        htmlFor="fesh-upload"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          onFiles(e.dataTransfer.files);
        }}
        data-testid="upload-dropzone"
        className="mt-8 block cursor-pointer rounded-3xl border-2 border-dashed border-slate-300 bg-white p-10 text-center hover:border-[#4DA6A1] transition-colors"
      >
        <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
          <Upload size={22} className="text-[#0F3D4A]" strokeWidth={2.25} />
        </div>
        <div className="mt-4 font-semibold text-[#0F3D4A]">
          Dosyayı buraya sürükleyin veya seçin
        </div>
        <div className="mt-1 text-sm text-[#0F3D4A]/60">
          PDF, JPG, PNG · En fazla 6 dosya
        </div>
        <input
          id="fesh-upload"
          type="file"
          multiple
          accept={UPLOAD_ACCEPT}
          className="hidden"
          data-testid="upload-input"
          onChange={(e) => onFiles(e.target.files)}
        />
      </label>

      {fileError && (
        <p role="alert" data-testid="upload-error" className="mt-3 text-sm font-semibold text-red-700">
          {fileError}
        </p>
      )}

      {files.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="upload-list">
          {files.map((f, i) => (
            <div
              key={i}
              className="flex items-center justify-between rounded-2xl bg-white border border-slate-200 px-4 py-3"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                  <FileText size={16} className="text-[#0F3D4A]" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#0F3D4A] truncate">
                    {f.name}
                  </div>
                  <div className="text-xs text-[#0F3D4A]/60">
                    {(f.size / 1024).toFixed(1)} KB
                  </div>
                </div>
              </div>
              <button
                data-testid={`remove-file-${i}`}
                onClick={() =>
                  setFiles((prev) => prev.filter((_, idx) => idx !== i))
                }
                className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
                aria-label="Dosyayı kaldır"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && (
        <div data-testid="no-document-option" className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[var(--feshet-muted)]">
          Belgem yok, devam et — Bu adım zorunlu değildir.
        </div>
      )}

      <div className="mt-6 rounded-2xl bg-slate-100/70 border border-slate-200 p-4 flex items-start gap-3">
        <ShieldAlert size={18} className="text-[#0F3D4A] mt-0.5 shrink-0" />
        <p className="text-xs text-[#0F3D4A]/75 leading-relaxed">
          Belgelerin içeriği sunucuya yüklenmez; yalnızca bu oturumdaki başvuru
          hazırlığında dosya adı ve boyutu kullanılır. Bu alana kimlik belgesi, banka bilgisi veya
          e-Devlet şifresi eklemeyin.
        </p>
      </div>
    </div>
  );
}

const Step5 = React.forwardRef(function Step5(
  { values, setValues, errors, setErrors, subscriptionField },
  ref
) {
  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };
  const fieldClass = (field) =>
    `mt-2 w-full min-w-0 rounded-2xl bg-white border px-4 py-3 text-[#0F3D4A] placeholder:text-[#0F3D4A]/40 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500 disabled:cursor-not-allowed ${
      errors[field] ? "border-red-500 focus:border-red-500" : "border-slate-200 focus:border-[#4DA6A1]"
    }`;
  const input = (id, label, props) => (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-[#0F3D4A]">{label}</label>
      <input
        id={id}
        aria-invalid={!!errors[id]}
        aria-describedby={errors[id] ? `${id}-error` : undefined}
        className={fieldClass(id)}
        {...props}
      />
      {errors[id] && <p id={`${id}-error`} className="mt-1.5 text-sm text-red-600" role="alert">{errors[id]}</p>}
    </div>
  );

  return (
    <form ref={ref} onSubmit={(event) => event.preventDefault()} noValidate>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">Başvuruyu kimin adına hazırlıyoruz?</h1>
      <p className="mt-2 text-[#0F3D4A]/70">Hazırlanan metinde kullanılacak iletişim ve abonelik bilgilerinizi girin.</p>

      <section className="mt-8 rounded-3xl border border-slate-200 bg-white/80 p-5 md:p-6">
        <h2 className="text-sm font-bold text-[var(--feshet-petrol)]">Sizi tanıyalım</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-5">
          {input("firstName", "Ad", { required: true, value: values.firstName, onChange: (event) => update("firstName", event.target.value), autoComplete: "given-name" })}
          {input("lastName", "Soyad", { required: true, value: values.lastName, onChange: (event) => update("lastName", event.target.value), autoComplete: "family-name" })}
        </div>
      </section>

      <section className="mt-4 rounded-3xl border border-slate-200 bg-white/80 p-5 md:p-6">
        <h2 className="text-sm font-bold text-[var(--feshet-petrol)]">İletişim bilgileriniz</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-5">
          {input("email", "E-posta", { required: true, type: "email", inputMode: "email", value: values.email, onChange: (event) => update("email", event.target.value), autoComplete: "email" })}
          {input("phone", "Telefon", { required: true, type: "tel", inputMode: "tel", value: values.phone, onChange: (event) => update("phone", event.target.value), autoComplete: "tel", placeholder: "05XXXXXXXXX" })}
        </div>
      </section>

      <section className="mt-4 rounded-3xl border border-slate-200 bg-white/80 p-5 md:p-6">
        <h2 className="text-sm font-bold text-[var(--feshet-petrol)]">Abonelik bilginiz</h2>
        <div className="mt-4">
          {input("subscriptionNumber", subscriptionField.label, {
            required: !values.subscriptionNumberUnknown,
            disabled: values.subscriptionNumberUnknown,
            value: values.subscriptionNumber,
            onChange: (event) => update("subscriptionNumber", event.target.value),
            autoComplete: "off",
            maxLength: 64,
            placeholder: subscriptionField.placeholder,
          })}
          <label className="mt-3 flex items-start gap-3 cursor-pointer text-sm text-[#0F3D4A]">
            <input
              type="checkbox"
              checked={values.subscriptionNumberUnknown}
              onChange={(event) => {
                setValues((current) => setSubscriptionUnknown(current, event.target.checked));
                setErrors((current) => ({ ...current, subscriptionNumber: undefined }));
              }}
              className="mt-0.5 h-4 w-4 accent-[#14A89F] shrink-0"
            />
            <span>Numaramı bilmiyorum</span>
          </label>
        </div>
      </section>

      <div className="mt-5 rounded-2xl border border-[var(--feshet-teal)]/20 bg-[var(--feshet-mint)]/45 p-4">
        <label className="flex items-start gap-3 cursor-pointer text-sm text-[#0F3D4A]">
          <input
            type="checkbox"
            checked={values.privacyAccepted}
            required
            aria-invalid={!!errors.privacyAccepted}
            aria-describedby={errors.privacyAccepted ? "privacyAccepted-error" : undefined}
            onChange={(event) => update("privacyAccepted", event.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[#14A89F] shrink-0"
          />
          <span><Link to="/kvkk-aydinlatma-metni" target="_blank" rel="noreferrer" className="font-semibold text-[#0F766E] underline">Aydınlatma metnini</Link>{" "}okudum.</span>
        </label>
        {errors.privacyAccepted && <p id="privacyAccepted-error" className="mt-1.5 text-sm text-red-600" role="alert">{errors.privacyAccepted}</p>}
      </div>
    </form>
  );
});

function FlowInformationFields({ flow, values, setValues }) {
  const config = FLOW_CONFIG[flow];
  if (!config) return null;
  return (
    <section data-testid="flow-information-fields" className="mt-5 rounded-3xl border border-slate-200 bg-white/80 p-5 md:p-6">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[var(--feshet-teal)]">
        <Brain size={15} /> Abonelik İptal Asistanı
      </div>
      <p className="mt-2 text-sm leading-relaxed text-[var(--feshet-muted)]">{config.agentNote}</p>
      <h2 className="mt-5 text-lg font-semibold text-[var(--feshet-text)]">İşlem bilgileri</h2>
      <p className="mt-1 text-sm text-[var(--feshet-muted)]">Yalnızca bildiğiniz alanları doldurun; bu alanlar zorunlu değildir.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {config.infoFields.map(([id, label, placeholder]) => (
          <div key={id} className={id === "faultDescription" ? "sm:col-span-2" : ""}>
            <label htmlFor={id} className="block text-sm font-semibold text-[#0F3D4A]">{label}</label>
            <input
              id={id}
              data-testid={`flow-info-${id}`}
              value={values[id] || ""}
              placeholder={placeholder}
              onChange={(event) => setValues((current) => ({ ...current, [id]: event.target.value }))}
              className="mt-2 w-full min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[#0F3D4A] focus:border-[#4DA6A1] focus:outline-none"
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function CommerceInformationFields({ values, setValues }) {
  return (
    <section data-testid="commerce-information-fields" className="mt-5 rounded-3xl border border-slate-200 bg-white/80 p-5 md:p-6">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[var(--feshet-teal)]"><PackageX size={15} /> Sipariş bilgileri</div>
      <p className="mt-2 text-sm leading-relaxed text-[var(--feshet-muted)]">Ürünü platform değil farklı bir mağaza sattıysa satıcı adını da ekleyin. Yalnız bildiğiniz alanları doldurun.</p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        {COMMERCE_INFO_FIELDS.map(([id, label, placeholder]) => (
          <div key={id} className={id === "platformResponse" ? "sm:col-span-2" : ""}>
            <label htmlFor={`commerce-${id}`} className="block text-sm font-semibold text-[#0F3D4A]">{label}</label>
            {id === "platformResponse" ? (
              <textarea id={`commerce-${id}`} data-testid={`commerce-info-${id}`} rows={3} value={values[id] || ""} placeholder={placeholder} onChange={(event) => setValues((current) => ({ ...current, [id]: event.target.value }))} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[#0F3D4A] focus:border-[#4DA6A1] focus:outline-none" />
            ) : (
              <input id={`commerce-${id}`} data-testid={`commerce-info-${id}`} value={values[id] || ""} placeholder={placeholder} onChange={(event) => setValues((current) => ({ ...current, [id]: event.target.value }))} className="mt-2 w-full min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-[#0F3D4A] focus:border-[#4DA6A1] focus:outline-none" />
            )}
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs leading-relaxed text-slate-500">Kart numaranızın tamamını veya platform şifrenizi yazmayın.</p>
    </section>
  );
}

function Step6({
  issue,
  flow,
  problemChoice,
  issueInfo,
  desiredOutcome,
  description,
  setDescription,
  selectedKeys,
  setSelectedKeys,
  freeText,
  setFreeText,
}) {
  if (issue === "iptal") {
    const compatibleDetails = getCompatibleCancellationAdditionalDetails(problemChoice);
    const updateGeneratedDescription = (keys, customText) => {
      setDescription(buildCancellationAdditionalDescription(keys, customText, problemChoice));
    };
    const toggleDetail = (key) => {
      const toggledKeys = selectedKeys.includes(key)
        ? selectedKeys.filter((selectedKey) => selectedKey !== key)
        : [...selectedKeys, key];
      const nextKeys = filterCompatibleCancellationAdditionalKeys(problemChoice, toggledKeys);
      const nextFreeText = key === "other" && !nextKeys.includes("other") ? "" : freeText;
      setSelectedKeys(nextKeys);
      if (nextFreeText !== freeText) setFreeText(nextFreeText);
      updateGeneratedDescription(nextKeys, nextFreeText);
    };
    const otherSelected = selectedKeys.includes("other");

    return (
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">Başka neler yaşadınız?</h1>
        <p className="mt-2 text-[#0F3D4A]/70">Varsa bir veya daha fazlasını seçin. Abonelik İptal bunları başvurunuza eklesin.</p>
        <div className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="additional-detail-options">
          {compatibleDetails.map((item) => {
            const active = selectedKeys.includes(item.key);
            return (
              <button
                key={item.key}
                type="button"
                data-testid={`additional-detail-${item.key}`}
                aria-pressed={active}
                onClick={() => toggleDetail(item.key)}
                className={`fs-card min-h-14 px-5 py-4 text-left font-semibold text-[#0F3D4A] ${active ? "fs-card-active" : ""}`}
              >
                <span className="flex items-start gap-3">
                  <span aria-hidden="true" className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs ${active ? "border-[#14A89F] bg-[#14A89F] text-white" : "border-slate-300 bg-white"}`}>{active ? "✓" : ""}</span>
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {otherSelected && (
          <div className="mt-5">
            <label htmlFor="additionalFreeText" className="block text-sm font-semibold text-[#0F3D4A]">Başka durumu kısaca anlatın</label>
            <textarea
              id="additionalFreeText"
              data-testid="additional-free-text"
              value={freeText}
              onChange={(event) => {
                const value = event.target.value;
                setFreeText(value);
                updateGeneratedDescription(selectedKeys, value);
              }}
              rows={3}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white p-4 text-[#0F3D4A] focus:outline-none focus:border-[#4DA6A1]"
            />
          </div>
        )}

        {selectedKeys.length === 0 ? (
          <div data-testid="skip-additional-details" className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[var(--feshet-muted)]">Detay eklemeden devam et</div>
        ) : (
          <div className="mt-6">
            <label htmlFor="description-textarea" className="block text-sm font-semibold text-[#0F3D4A]">Başvuruya eklenecek yardımcı metin</label>
            <textarea
              id="description-textarea"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              data-testid="description-textarea"
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white p-4 text-[#0F3D4A] focus:outline-none focus:border-[#4DA6A1]"
            />
            <p className="mt-2 text-xs text-slate-500">Metin otomatik oluşturuldu; isterseniz düzenleyebilirsiniz.</p>
          </div>
        )}
      </div>
    );
  }

  const config = FLOW_CONFIG[flow];
  const additionalOptions = (config?.problemOptions || []).filter((option) => option.id !== problemChoice);
  const toggle = (id) => setSelectedKeys((current) => (
    current.includes(id) ? current.filter((key) => key !== id) : [...current, id]
  ));
  const preview = buildFlowDescription({
    flow,
    problemLabel: config?.problemOptions.find((option) => option.id === problemChoice)?.label,
    desiredOutcome: desiredOutcome?.sentence,
    info: issueInfo,
    selectedLabels: additionalOptions.filter((option) => selectedKeys.includes(option.id)).map((option) => option.label),
    freeText,
  });
  return (
    <div>
      <h1 className="text-3xl md:text-4xl font-bold text-[#0F3D4A] tracking-tight">Başka neler yaşadınız?</h1>
      <p className="mt-2 text-[#0F3D4A]/70">Varsa ek durumları seçin. Seçmedikleriniz başvuru metnine eklenmez.</p>
      <div className="mt-7 grid gap-3 sm:grid-cols-2" data-testid="additional-detail-options">
        {additionalOptions.map((option) => {
          const active = selectedKeys.includes(option.id);
          return (
            <button key={option.id} type="button" data-testid={`additional-detail-${option.id}`} aria-pressed={active} onClick={() => toggle(option.id)} className={`fs-card min-h-14 px-5 py-4 text-left font-semibold text-[#0F3D4A] ${active ? "fs-card-active" : ""}`}>
              <span className="flex items-start gap-3"><span aria-hidden="true" className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-xs ${active ? "border-[#14A89F] bg-[#14A89F] text-white" : "border-slate-300 bg-white"}`}>{active ? "✓" : ""}</span>{option.label}</span>
            </button>
          );
        })}
      </div>
      <label htmlFor="additionalFreeText" className="mt-5 block text-sm font-semibold text-[#0F3D4A]">Eklemek istediğiniz başka bir durum (isteğe bağlı)</label>
      <textarea id="additionalFreeText" data-testid="additional-free-text" value={freeText} onChange={(event) => setFreeText(event.target.value)} rows={4} className="mt-2 w-full rounded-2xl border border-slate-200 bg-white p-4 text-[#0F3D4A] focus:border-[#4DA6A1] focus:outline-none" />
      {preview && <div data-testid="flow-description-preview" className="mt-5 rounded-2xl border border-teal-100 bg-teal-50 p-4 text-sm leading-relaxed text-slate-700">{preview}</div>}
      {!selectedKeys.length && !freeText.trim() && <div data-testid="skip-additional-details" className="mt-5 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-[var(--feshet-muted)]">Detay eklemeden devam et</div>}
    </div>
  );
}

function ReviewDialog({
  open,
  onOpenChange,
  onConfirm,
  issue,
  company,
  problemDetail,
  files,
  applicationInfo,
  subscriptionField,
  loading,
}) {
  const issueLabel = ISSUE_TYPES.find((item) => item.id === issue)?.title || issue;
  const rows = [
    ["İşlem", issueLabel],
    ["Kurum", company],
    ["Sorun", getProblemDisplayLabel(issue, problemDetail)],
    ["Yüklenen belge", `${files.length} dosya`],
    ["Başvuru sahibi", `${applicationInfo.firstName} ${applicationInfo.lastName}`.trim()],
    [
      subscriptionField.label,
      applicationInfo.subscriptionNumberUnknown ? "Numaramı bilmiyorum" : "Girildi",
    ],
  ];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="review-dialog" className="w-[calc(100%_-_2rem)] max-w-xl rounded-[2rem] border-slate-200 bg-[var(--feshet-surface)] p-6 md:p-8">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-2xl text-[var(--feshet-text)]">Başvurunuzu kontrol edin</AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--feshet-muted)]">Hazırlamadan önce seçimlerinizi son kez gözden geçirin.</AlertDialogDescription>
        </AlertDialogHeader>
        <dl className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white px-4">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-3 py-3 text-sm">
              <dt className="font-semibold text-[var(--feshet-muted)]">{label}</dt>
              <dd className="text-right font-bold text-[var(--feshet-text)] break-words">{value || "—"}</dd>
            </div>
          ))}
        </dl>
        <AlertDialogFooter className="mt-2 gap-2">
          <AlertDialogCancel data-testid="review-edit-btn" className="rounded-full">Düzenle</AlertDialogCancel>
          <AlertDialogAction
            data-testid="review-submit-btn"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-full bg-[var(--feshet-teal)] hover:bg-[var(--feshet-petrol)]"
          >
            Başvurumu hazırla
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ---------- Result ---------- */

export function ResultPage({ result, onReset, context }) {
  const resultNavigate = useNavigate();
  const institutionContext = getCompanyInstitutionContext(context.company);
  const isDigitalSubscription = isDigitalInstitutionContext(institutionContext);
  const isEcommerce = isEcommerceInstitutionContext(institutionContext);
  const flowConfig = FLOW_CONFIG[context.flow];
  const selectedAdditionalLabels = (flowConfig?.problemOptions || [])
    .filter((option) => context.additionalDetailKeys?.includes(option.id))
    .map((option) => option.label);
  const baseFlowDescription = flowConfig ? buildFlowDescription({
    flow: context.flow,
    selectedLabels: selectedAdditionalLabels,
    freeText: context.additionalFreeText,
  }) : context.description;
  // E-ticaret alanları ve seçilen çözüm aşağıda yapılandırılmış alanlardan eklenir.
  // Bunları açıklamaya da kopyalamak aynı bilgiyi sonuç metninde iki kez üretirdi.
  const flowDescription = baseFlowDescription;
  const preparedText = isEcommerce
    ? createV1ApplicationEmailBody({
      flow: "refund",
      company: context.company,
      problemLabel: getCommerceProblemOption(context.commerceProblem)?.label || context.problemLabel,
      description: flowDescription,
      desiredOutcome: context.desiredOutcome?.sentence,
      commerceProblem: context.commerceProblem,
      info: { ...(context.issueInfo || {}), ...(context.commerceInfo || {}) },
      applicant: context.applicationInfo,
      subscriptionLabel: context.subscriptionField?.label,
      subscriptionNumber: context.applicationInfo?.subscriptionNumberUnknown ? "" : context.applicationInfo?.subscriptionNumber,
    })
    : context.issue === "iptal"
    ? createCancellationEmailBody({
      company: context.company,
      problemLabel: context.problemLabel,
      description: context.description,
      applicant: context.applicationInfo,
      subscriptionLabel: context.subscriptionField?.label,
      subscriptionNumber: context.applicationInfo?.subscriptionNumberUnknown
        ? ""
        : context.applicationInfo?.subscriptionNumber,
    })
    : flowConfig ? createV1ApplicationEmailBody({
      flow: context.flow,
      company: context.company,
      problemLabel: context.problemLabel,
      description: flowDescription,
      desiredOutcome: context.desiredOutcome?.sentence,
      info: context.issueInfo,
      applicant: context.applicationInfo,
      subscriptionLabel: context.subscriptionField?.label,
      subscriptionNumber: context.applicationInfo?.subscriptionNumberUnknown ? "" : context.applicationInfo?.subscriptionNumber,
    }) : result.generated_text;
  const [copyLabel, setCopyLabel] = useState("Metni Kopyala");
  const [enriching, setEnriching] = useState(false);
  const [displayText, setDisplayText] = useState(() => (
    context.issue === "iptal"
      ? stripDigitalCancellationSignature(preparedText)
      : stripDigitalSignature(preparedText)
  ));
  const [enriched, setEnriched] = useState(false);
  const [enrichFailed, setEnrichFailed] = useState(false);
  const [deliveryState, setDeliveryState] = useState(DELIVERY_STATES.PREPARED);
  const [openedChannel, setOpenedChannel] = useState(null);
  const [sentRecord, setSentRecord] = useState(null);
  const deliveryProfile = getInstitutionDeliveryProfile(
    context.issue,
    institutionContext?.companyId || context.company,
    {
      serviceScope: institutionContext?.serviceScope,
      billingProvider: context.billingProvider,
      commerceProblem: context.commerceProblem,
      purchaseAccountMode: context.purchaseAccountMode,
    }
  );
  const subscriptionNumber = context.applicationInfo?.subscriptionNumberUnknown
    ? ""
    : context.applicationInfo?.subscriptionNumber || "";
  const emailSubject = createEmailSubject(
    context.issue,
    deliveryProfile?.name || context.company,
    subscriptionNumber,
    {
      flow: context.flow,
      transactionNumber: context.commerceInfo?.transactionNumber || context.issueInfo?.transactionNumber,
      commerceProblem: context.commerceProblem,
    }
  );

  useEffect(() => {
    if (deliveryState === DELIVERY_STATES.SUBMITTED_CONFIRMED && sentRecord) {
      setDeliveryState(DELIVERY_STATES.AWAITING_RESPONSE);
    }
  }, [deliveryState, sentRecord]);

  const handleChannelOpened = (channel) => {
    setOpenedChannel(channel);
    if (!sentRecord) setDeliveryState(DELIVERY_STATES.CHANNEL_OPENED);
  };

  const confirmSubmission = () => {
    if (deliveryState !== DELIVERY_STATES.CHANNEL_OPENED || !openedChannel) return;
    setDeliveryState(DELIVERY_STATES.SUBMITTED_CONFIRMED);
    setSentRecord(createConfirmedSubmissionRecord({
      company: context.company,
      issue: context.problemLabel,
      channel: openedChannel.label,
      channelKind: openedChannel.kind,
      reference: "",
      attachmentCount: context.files?.length || 0,
    }));
  };

  const copy = async () => {
    const copied = await copyTextSafely(displayText);
    if (copied) {
      setCopyLabel("Kopyalandı ✓");
      toast.success("Başvuru metni kopyalandı.", { id: "result-text-copy" });
      setTimeout(() => setCopyLabel("Metni Kopyala"), 1800);
    } else {
      toast.error("Kopyalama yapılamadı. Metni seçerek kopyalayabilirsiniz.", {
        id: "result-text-copy",
      });
    }
  };

  const enrich = async () => {
    if (context.hasNationalId) {
      toast.message("Kimlik bilginizi korumak için bu metin ek bir servise gönderilmedi.", {
        description: "Hazır metni doğrudan kopyalayabilirsiniz.",
      });
      return;
    }
    setEnriching(true);
    try {
      const { data } = await axios.post(`${API}/case/enrich`, {
        generated_text: displayText,
        company: context.company,
        problem_detail: context.problemDetail,
        user_description: context.description,
        issue_type: context.issue,
      }, { timeout: 25000 });
      if (data?.enriched_text) {
        setDisplayText(context.issue === "iptal"
          ? stripDigitalCancellationSignature(data.enriched_text)
          : stripDigitalSignature(data.enriched_text));
        setEnriched(true);
        toast.success("Metin AI ile iyileştirildi");
      } else {
        // No text returned — treat as soft failure, keep original.
        setEnrichFailed(true);
        toast.message("AI iyileştirme şu anda kullanılamıyor", {
          description: "Hazır metni yine de kopyalayabilirsiniz.",
        });
      }
    } catch {
      // Any network / 5xx / model error → graceful fallback.
      setEnrichFailed(true);
      toast.message("AI iyileştirme şu anda kullanılamıyor", {
        description: "Hazır metni yine de kopyalayabilirsiniz.",
      });
    } finally {
      setEnriching(false);
    }
  };

  return (
    <div data-testid="result-page" data-delivery-state={deliveryState}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-2">
            Gönderim adımı
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] tracking-tight">
            {isDigitalSubscription || isEcommerce ? "Resmî işlem kanalınız hazır" : "Başvurunuz hazır"}
          </h1>
          <p className="mt-2 text-slate-600">
            {isDigitalSubscription
              ? "Doğru resmî sayfayı açacağız; iptal veya iade işlemini platformda siz tamamlayacaksınız."
              : isEcommerce
                ? "Platformun doğrulanmış sipariş veya iade kanalını açacağız; talebi siz tamamlayacaksınız."
              : flowConfig?.resultSubtitle || "Şimdi uygun kanaldan göndererek işlemi tamamlayın."}
          </p>
          <p className="mt-1 text-sm font-semibold text-[#0F766E]">{context.company} · {context.problemLabel}</p>
        </div>
        <button
          onClick={onReset}
          data-testid="result-reset-btn"
          className="fs-btn-outline inline-flex items-center gap-2 text-sm"
        >
          <RotateCcw size={16} /> Yeni başvuru
        </button>
      </div>

      <OfficialChannelsCard
        issueType={context.issue}
        company={context.company}
        companyId={institutionContext?.companyId}
        serviceScope={institutionContext?.serviceScope}
        billingProvider={context.billingProvider}
        commerceProblem={context.commerceProblem}
        purchaseAccountMode={context.purchaseAccountMode}
        generatedText={displayText}
        onGeneratedTextChange={setDisplayText}
        hasNationalId={context.hasNationalId}
        subscriptionNumber={subscriptionNumber}
        attachmentCount={context.files?.length || 0}
        attachments={context.files || []}
        senderEmail={context.applicationInfo?.email || ""}
        onChannelOpened={handleChannelOpened}
        flow={context.flow}
        transactionNumber={context.commerceInfo?.transactionNumber || context.issueInfo?.transactionNumber || ""}
      />

      {context.issue !== "hakem" && openedChannel && deliveryState === DELIVERY_STATES.CHANNEL_OPENED && !sentRecord && (
        <DeliveryConfirmation
          channel={openedChannel}
          onNotYet={() => setDeliveryState(DELIVERY_STATES.CHANNEL_OPENED)}
          onSent={confirmSubmission}
        />
      )}

      {context.issue !== "hakem" && sentRecord && isDigitalSubscription && (
        <section data-testid="digital-self-reported-completion" className="mt-5 rounded-3xl border border-teal-200 bg-teal-50 p-5 md:p-6">
          <h2 className="text-xl font-semibold text-[#0F172A]">Talebi oluşturduğunuzu belirttiniz</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Bu yalnızca sizin açık onayınıza dayalı yerel bir kayıttır. Abonelik İptal iptal veya iade sonucunu doğrulamaz; güncel durumu platform hesabınızdan kontrol edin.
          </p>
        </section>
      )}

      {context.issue !== "hakem" && sentRecord && (
        <ApplicationTracking
          record={sentRecord}
          onReferenceChange={(value) => setSentRecord((current) => ({ ...current, reference: value }))}
          onEscalate={() => resultNavigate(`/dosya-baslat?issue=hakem&company=${encodeURIComponent(context.company)}&source=escalation`)}
          onStatusChange={(status) => setDeliveryState(status === "completed"
            ? DELIVERY_STATES.COMPLETED
            : DELIVERY_STATES.AWAITING_RESPONSE)}
        />
      )}

      <details data-testid="application-details" className="mt-8 rounded-3xl border border-slate-200 bg-white p-5 md:p-6">
        <summary className="cursor-pointer text-lg font-semibold text-[#0F172A]">Başvuru detayları</summary>
        {result.assessment && (
          <AgentAssessment assessment={result.assessment} />
        )}
        {context.issue !== "hakem" && !isDigitalSubscription && (
          <PreparedApplicationOverview
            emailSubject={emailSubject}
            emailBody={displayText}
            files={context.files}
            applicationInfo={context.applicationInfo}
            subscriptionField={context.subscriptionField}
            followUpPlan={result.follow_up_plan}
          />
        )}
      <div className="mt-8">
        <Tabs defaultValue="summary" className="w-full">
          <TabsList
            data-testid="result-tabs"
            className="flex flex-wrap gap-1 h-auto bg-slate-100 p-1 rounded-full"
          >
            {[
              { v: "summary", l: "Durum Özeti", icon: ClipboardList },
              { v: "path", l: "En Doğru Yol", icon: Route },
              { v: "text", l: "Hazır Metin", icon: FileText },
              { v: "docs", l: "Gerekli Belgeler", icon: ClipboardList },
              { v: "plan", l: "Takip Planı", icon: CalendarClock },
            ].map((t) => (
              <TabsTrigger
                key={t.v}
                value={t.v}
                data-testid={`tab-${t.v}`}
                className="rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-white data-[state=active]:text-[#0F172A] data-[state=active]:shadow-sm text-slate-500"
              >
                {t.l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="summary" className="mt-6">
            <div className="fs-card p-6 md:p-8">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-3">
                Durum Özeti
              </h3>
              <pre className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed text-sm md:text-base">
                {result.summary}
              </pre>
            </div>
          </TabsContent>

          <TabsContent value="path" className="mt-6">
            <div className="fs-card p-6 md:p-8">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                Önerilen adımlar
              </h3>
              <ol className="space-y-3">
                {result.recommended_path.map((s, i) => (
                  <li
                    key={i}
                    data-testid={`path-step-${i}`}
                    className="flex gap-3 items-start"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#0F172A] text-white text-xs font-bold flex items-center justify-center mt-0.5 shrink-0">
                      {i + 1}
                    </div>
                    <div className="text-slate-700 leading-relaxed">{s}</div>
                  </li>
                ))}
              </ol>
            </div>
          </TabsContent>

          <TabsContent value="text" className="mt-6">
            <div className="fs-card p-6 md:p-8">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F]">
                    Abonelik İptal · Hazır Belge
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mt-1">
                    Hazır Metin
                  </h3>
                </div>
                <div className="flex gap-2">
                  {!enrichFailed && !flowConfig && (
                    <button
                      onClick={enrich}
                      disabled={enriching}
                      data-testid="enrich-btn"
                      className="fs-btn-outline inline-flex items-center gap-2 text-sm disabled:opacity-60"
                    >
                      {enriching ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />{" "}
                          İyileştiriliyor
                        </>
                      ) : (
                        <>
                          <Sparkles size={14} />{" "}
                          {enriched ? "Yeniden iyileştir" : "Metni iyileştir"}
                        </>
                      )}
                    </button>
                  )}
                  <button
                    onClick={copy}
                    data-testid="copy-text-btn"
                    className="fs-btn-primary inline-flex items-center gap-2 text-sm"
                  >
                    <Copy size={14} /> {copyLabel}
                  </button>
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-slate-200 shadow-inner">
                <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 text-xs text-slate-500">
                  <div className="w-2 h-2 rounded-full bg-[#E76A5A]" />
                  <div className="w-2 h-2 rounded-full bg-[#F5C560]" />
                  <div className="w-2 h-2 rounded-full bg-[#14A89F]" />
                  <span className="ml-2 font-semibold text-slate-600">
                    dilekce.txt
                  </span>
                </div>
                <pre
                  data-testid="generated-text"
                  className="whitespace-pre-wrap font-sans text-slate-800 leading-relaxed p-5 md:p-6 text-sm md:text-[15px]"
                >
                  {displayText}
                </pre>
              </div>
              <div className="mt-4 flex items-start gap-3 rounded-2xl bg-teal-50 border border-teal-100 p-4">
                <Send size={16} className="text-[#0F8F87] mt-0.5 shrink-0" />
                <p className="text-sm text-slate-700 leading-relaxed">
                  {result.send_hint ||
                    "Bu metni ilgili kurumun müşteri hizmetleri formuna, e-posta kanalına veya platform destek talebine ekleyebilirsiniz. Göndermeden önce metni kontrol edin."}
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="docs" className="mt-6">
            <div className="fs-card p-6 md:p-8">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                Yanınızda bulunmalı
              </h3>
              <ul className="space-y-2.5">
                {result.required_documents.map((d, i) => (
                  <li
                    key={i}
                    data-testid={`doc-${i}`}
                    className="flex items-start gap-3 rounded-2xl bg-slate-50 border border-slate-100 px-4 py-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#14A89F] text-white text-xs flex items-center justify-center mt-0.5 shrink-0">
                      ✓
                    </div>
                    <span className="text-slate-800">{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="plan" className="mt-6">
            <div className="fs-card p-6 md:p-8">
              <h3 className="text-lg font-semibold text-[#0F172A] mb-4">
                Takip planınız
              </h3>
              <ol className="relative border-l-2 border-dashed border-slate-200 pl-6 space-y-6">
                {result.follow_up_plan.map((p, i) => (
                  <li key={i} data-testid={`plan-step-${i}`}>
                    <span className="absolute -left-[9px] w-4 h-4 rounded-full bg-[#14A89F] ring-4 ring-teal-50" />
                    <div className="text-xs font-bold uppercase tracking-widest text-[#14A89F]">
                      {p.label}
                    </div>
                    <div className="text-lg font-semibold text-[#0F172A]">
                      {p.date}
                    </div>
                    <div className="text-sm text-slate-600 mt-1 leading-relaxed">
                      {p.note}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {result.reason_bullets && result.reason_bullets.length > 0 && (
        <ReasonSection bullets={result.reason_bullets} />
      )}
      </details>

      <div
        data-testid="result-disclaimer"
        className="mt-8 rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-3"
      >
        <ShieldAlert size={18} className="text-[#E76A5A] mt-0.5 shrink-0" />
        <p className="text-sm text-slate-600 leading-relaxed">
          {result.disclaimer}
        </p>
      </div>

    </div>
  );
}

function DeliveryConfirmation({
  channel,
  onSent,
  onNotYet,
}) {
  const isEmail = channel.kind === DELIVERY_CHANNEL_KINDS.EMAIL;
  return (
    <section data-testid="delivery-confirmation" data-delivery-state={DELIVERY_STATES.CHANNEL_OPENED} className="mt-5 rounded-3xl border border-teal-200 bg-teal-50 p-5 md:p-6">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#0F8F87]">Gönderim kontrolü</div>
      <h2 className="mt-2 text-xl font-semibold text-[#0F172A]">{isEmail ? "E-posta ekranını açtık" : "Resmî sayfayı açtık"}</h2>
      <p className="mt-2 text-sm text-slate-700">{isEmail
        ? "E-postayı gönderip işlemi tamamladınız mı?"
        : "Talebinizi resmî sayfada tamamladınız mı?"}</p>
      <p className="mt-1 text-sm text-slate-600">Açılan kanal: {channel.label}</p>
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <button type="button" data-testid="confirm-sent-btn" onClick={onSent} className="fs-btn-primary min-h-11">{isEmail ? "Evet, gönderdim" : "Evet, talebi oluşturdum"}</button>
        <button type="button" data-testid="delivery-not-yet-btn" onClick={onNotYet} className="fs-btn-outline min-h-11">{isEmail ? "Henüz göndermedim" : "Henüz tamamlamadım"}</button>
        <button type="button" data-testid="delivery-reopen-btn" onClick={channel.reopen} className="fs-btn-outline min-h-11">{isEmail ? "E-posta ekranını tekrar aç" : "Resmî sayfayı tekrar aç"}</button>
      </div>
    </section>
  );
}

function PreparedApplicationOverview({
  emailSubject,
  emailBody,
  files = [],
  applicationInfo = {},
  subscriptionField,
  followUpPlan = [],
}) {
  const applicantRows = [
    ["Ad Soyad", [applicationInfo.firstName, applicationInfo.lastName].filter(Boolean).join(" ")],
    ["E-posta", applicationInfo.email],
    ["Telefon", applicationInfo.phone],
    [subscriptionField?.label || "Abone / Müşteri Numarası", applicationInfo.subscriptionNumberUnknown
      ? "Bilinmiyor"
      : applicationInfo.subscriptionNumber],
  ].filter(([, value]) => String(value || "").trim());

  return (
    <section data-testid="prepared-application-overview" className="mt-8 fs-card p-6 md:p-8">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F]">
        <BadgeCheck size={14} /> Gönderim kontrolü
      </div>
      <h2 className="mt-2 text-xl md:text-2xl font-semibold text-[#0F172A]">Başvuru içeriği</h2>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">E-posta konusu</div>
          <p data-testid="prepared-email-subject" className="mt-2 font-semibold text-[#0F172A] break-words">{emailSubject}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Belgeler</div>
          <p className="mt-2 text-sm text-slate-700">
            {files.length ? `${files.length} belge seçildi: ${files.map((file) => file.name).join(", ")}` : "Belge eklenmedi."}
          </p>
        </div>
      </div>
      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Kısa e-posta gövdesi</div>
        <pre data-testid="prepared-email-body" className="mt-3 whitespace-pre-wrap font-sans text-sm leading-relaxed text-slate-700">{emailBody}</pre>
      </div>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Başvuru sahibi</div>
          <dl className="mt-3 space-y-2 text-sm">
            {applicantRows.map(([label, value]) => (
              <div key={label} className="flex items-start justify-between gap-3">
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-right font-semibold text-slate-800 break-all">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Takip planı</div>
          <p className="mt-3 text-sm font-semibold text-slate-800">{followUpPlan[0]?.label || "Gönderim sonrası kontrol"}</p>
          <p className="mt-1 text-sm text-slate-600">{followUpPlan[0]?.note || "Başvuru kaydını ve kurum yanıtını saklayın."}</p>
        </div>
      </div>
    </section>
  );
}

function ApplicationTracking({ record, onReferenceChange, onEscalate, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [responseFile, setResponseFile] = useState(null);
  const [responseFileError, setResponseFileError] = useState("");

  return (
    <section data-testid="application-tracking" className="mt-8 fs-card p-6 md:p-8">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F]">
        <Clock3 size={14} /> Başvuru takibi
      </div>
      <h2 className="mt-2 text-2xl font-semibold text-[#0F172A]">
        Başvuru gönderildi · Yanıt bekleniyor
      </h2>
      <dl className="mt-5 grid gap-3 rounded-2xl bg-slate-50 p-4 text-sm sm:grid-cols-2">
        {[
          ["Kurum", record.company],
          ["İşlem", record.issue],
          ["Kanal", record.channel],
          ["Gönderim tarihi", <span data-testid="confirmed-sent-at">{record.sentAt}</span>],
          ["Belge sayısı", String(record.attachmentCount)],
          ["Sonraki kontrol", record.nextCheck],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-slate-500">{label}</dt>
            <dd className="mt-0.5 font-semibold text-slate-800">{value}</dd>
          </div>
        ))}
      </dl>
      <label htmlFor="trackingReference" className="mt-4 block text-sm font-semibold text-[#0F3D4A]">Başvuru numarası (varsa)</label>
      <input
        id="trackingReference"
        value={record.reference}
        onChange={(event) => onReferenceChange(event.target.value)}
        className="mt-2 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-[#4DA6A1]"
      />

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" aria-label="Başvuru sonucu">
        <button type="button" data-testid="tracking-response-btn" onClick={() => { setStatus("response"); onStatusChange?.("response"); }} className="fs-btn-outline min-h-11 inline-flex items-center justify-center gap-2"><MessageSquareText size={16} /> Yanıt geldi</button>
        <button type="button" data-testid="tracking-rejected-btn" onClick={() => { setStatus("rejected"); onStatusChange?.("rejected"); }} className="fs-btn-outline min-h-11 inline-flex items-center justify-center gap-2"><AlertTriangle size={16} /> Kurum reddetti</button>
        <button type="button" data-testid="tracking-no-response-btn" onClick={() => { setStatus("no-response"); onStatusChange?.("no-response"); }} className="fs-btn-outline min-h-11 inline-flex items-center justify-center gap-2"><Clock3 size={16} /> Cevap verilmedi</button>
        <button type="button" data-testid="tracking-completed-btn" onClick={() => { setStatus("completed"); onStatusChange?.("completed"); }} className="fs-btn-primary min-h-11 inline-flex items-center justify-center gap-2"><CheckCircle2 size={16} /> İşlem tamamlandı</button>
      </div>

      {status === "response" && (
        <div data-testid="tracking-response-form" className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
          <label htmlFor="institutionResponse" className="text-sm font-semibold text-[#0F3D4A]">Kurum yanıtını kısaca ekleyin</label>
          <textarea id="institutionResponse" value={responseText} onChange={(event) => setResponseText(event.target.value)} rows={4} className="mt-2 w-full rounded-2xl border border-slate-200 p-3 focus:outline-none focus:border-[#4DA6A1]" />
          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-[#0F766E]">
            <Paperclip size={15} /> Yanıt dosyası seç
            <input
              type="file"
              accept={UPLOAD_ACCEPT}
              className="hidden"
              data-testid="tracking-response-upload"
              onChange={(event) => {
                const { acceptedFiles, error } = validateSelectedFiles(event.target.files);
                setResponseFileError(error);
                setResponseFile(acceptedFiles[0] || null);
              }}
            />
          </label>
          {responseFileError && <p role="alert" data-testid="tracking-upload-error" className="mt-2 text-sm font-semibold text-red-700">{responseFileError}</p>}
          {responseFile && <p className="mt-2 text-xs text-slate-500">{responseFile.name}</p>}
        </div>
      )}

      {(status === "rejected" || status === "no-response") && (
        <div data-testid="tracking-escalation" className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-semibold text-[#0F172A]">Bir üst başvurunuzu hazırlayın</h3>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-700">İlk kurum başvurunuz, gönderim tarihiniz ve belgeleriniz bir üst başvurunun hazırlanmasında kullanılabilir.</p>
          <button type="button" data-testid="prepare-arbitration-btn" onClick={onEscalate} className="fs-btn-primary mt-4">Hakem Heyeti dosyasını hazırla</button>
        </div>
      )}

      {status === "completed" && (
        <div data-testid="tracking-completed" className="mt-5 rounded-2xl border border-teal-200 bg-teal-50 p-5">
          <h3 className="font-semibold text-[#0F172A]">Sorun çözüldü</h3>
          <p className="mt-1.5 text-sm text-slate-700">Başvuru bu oturumdaki takip ekranında tamamlandı olarak işaretlendi.</p>
        </div>
      )}
    </section>
  );
}

/* ---------- Agent components ---------- */

const ANALYZE_STEPS = [
  { label: "Sorun türü analiz ediliyor", icon: ScanSearch },
  { label: "Kurum ve platform eşleştiriliyor", icon: Building2 },
  { label: "En doğru yol belirleniyor", icon: Route },
  { label: "Hazır metin oluşturuluyor", icon: FileText },
  { label: "Takip planı hazırlanıyor", icon: CalendarClock },
];

function AgentAnalyzing() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setActive((a) => (a + 1) % ANALYZE_STEPS.length);
    }, 480);
    return () => clearInterval(interval);
  }, []);
  return (
    <div data-testid="agent-analyzing" className="fs-card p-8 md:p-10">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-[#0F172A] text-white flex items-center justify-center">
          <Brain size={20} />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F]">
            Abonelik İptal Asistanı
          </div>
          <div className="text-lg font-semibold text-[#0F172A]">
            Başvurunuz hazırlanıyor
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#14A89F] fs-agent-pulse" />
          <span className="text-xs font-semibold text-slate-600">Analiz</span>
        </div>
      </div>
      <ul className="space-y-2.5">
        {ANALYZE_STEPS.map((s, i) => {
          const isDone = i < active;
          const isActive = i === active;
          const Icon = s.icon;
          return (
            <li
              key={i}
              data-testid={`analyze-step-${i}`}
              className={`flex items-center gap-3 rounded-2xl border px-4 py-3 transition-all duration-300 ${
                isActive
                  ? "border-[#14A89F] bg-teal-50"
                  : isDone
                  ? "border-slate-200 bg-white"
                  : "border-slate-100 bg-slate-50"
              }`}
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  isDone
                    ? "bg-[#14A89F] text-white"
                    : isActive
                    ? "bg-[#0F172A] text-white"
                    : "bg-white text-slate-400 border border-slate-200"
                }`}
              >
                {isDone ? <CheckCircle2 size={16} /> : <Icon size={16} />}
              </div>
              <span
                className={`text-sm font-semibold ${
                  isDone ? "text-slate-500 line-through" : "text-[#0F172A]"
                }`}
              >
                {s.label}
              </span>
              {isActive && (
                <span className="ml-auto text-xs font-semibold text-[#14A89F]">
                  işleniyor...
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="mt-6 h-1.5 rounded-full fs-shimmer" />
      <p className="mt-4 text-xs text-slate-500">
        Abonelik İptal Asistanı, seçimlerinizi ve varsa yüklenen belgelerinizi kural
        tabanlı analizden geçirir. Yaklaşık 2-3 saniye.
      </p>
    </div>
  );
}

const URGENCY_STYLE = {
  Yüksek: "fs-urgency-high",
  Orta: "fs-urgency-med",
  Düşük: "fs-urgency-low",
};

function AgentAssessment({ assessment }) {
  const {
    issue_label,
    company,
    urgency,
    urgency_reason,
    prep_status,
    prep_hint,
    estimated_days,
    first_action,
  } = assessment;
  return (
    <div
      data-testid="agent-assessment"
      className="mt-6 rounded-3xl bg-[#0F172A] text-white p-6 md:p-8 relative overflow-hidden"
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center">
          <Brain size={20} className="text-[#22D3CE]" />
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#22D3CE]">
            Asistan değerlendirmesi
          </div>
          <div className="text-lg md:text-xl font-semibold">
            {issue_label} · {company}
          </div>
        </div>
      </div>
      <div className="mt-6 grid md:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
            Aciliyet
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${URGENCY_STYLE[urgency] || "fs-urgency-med"}`}
              data-testid="assess-urgency"
            >
              {urgency}
            </span>
          </div>
          <div className="mt-2 text-xs text-white/70 leading-relaxed">
            {urgency_reason}
          </div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
            Hazırlık Durumu
          </div>
          <div
            className="mt-2 text-sm font-semibold text-white flex items-center gap-2"
            data-testid="assess-prep"
          >
            <BadgeCheck size={16} className="text-[#22D3CE]" /> {prep_status}
          </div>
          <div className="mt-2 text-xs text-white/70 leading-relaxed">
            {prep_hint}
          </div>
        </div>
        <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
            Tahmini Takip
          </div>
          <div
            className="mt-2 text-sm font-semibold text-white flex items-center gap-2"
            data-testid="assess-eta"
          >
            <CalendarClock size={16} className="text-[#22D3CE]" /> {estimated_days}
          </div>
          <div className="mt-2 text-xs text-white/70 leading-relaxed">
            Ortalama süreç süresi. Aciliyete göre değişebilir.
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-2xl bg-white/5 border border-white/10 p-4 flex items-start gap-3">
        <Lightbulb size={16} className="text-[#22D3CE] mt-0.5 shrink-0" />
        <div>
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">
            Önerilen ilk aksiyon
          </div>
          <div className="mt-1 text-sm text-white leading-relaxed">
            {first_action}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReasonSection({ bullets }) {
  return (
    <section
      data-testid="reason-section"
      className="mt-8 fs-card p-6 md:p-8"
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F]">
        <Lightbulb size={14} /> Abonelik İptal Asistanı · Açıklama
      </div>
      <h3 className="mt-2 text-xl md:text-2xl font-semibold text-[#0F172A]">
        Neden bu yolu önerdik?
      </h3>
      <ul className="mt-5 space-y-3">
        {bullets.map((b, i) => (
          <li
            key={i}
            data-testid={`reason-bullet-${i}`}
            className="flex items-start gap-3"
          >
            <div className="w-6 h-6 rounded-full bg-teal-50 text-[#0F8F87] text-xs font-bold flex items-center justify-center mt-0.5 shrink-0">
              {i + 1}
            </div>
            <div className="text-slate-700 leading-relaxed">{b}</div>
          </li>
        ))}
      </ul>
    </section>
  );
}
